"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Link2, Mail, Share2 } from "lucide-react";
import { cn } from "@/lib/format";

type IconProps = { size?: number; className?: string };

// lucide-react dropped its brand glyphs, so inline the marks we need to match
// the rest of the icon set (currentColor + size prop).
function FacebookIcon({ size = 16, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.9h-2.34V22c4.78-.76 8.44-4.92 8.44-9.94Z" />
    </svg>
  );
}

function XIcon({ size = 16, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M18.9 2h3.3l-7.2 8.23L23.6 22h-6.6l-5.18-6.77L5.9 22H2.6l7.7-8.8L2 2h6.78l4.68 6.19L18.9 2Zm-1.16 18h1.83L7.34 3.9H5.38L17.74 20Z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 16, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.69 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" />
    </svg>
  );
}

/** Builds the platform share URLs from a page URL + title. Shared by every SocialShare variant. */
function buildShareLinks(url: string, title: string) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  return {
    facebook: {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: FacebookIcon,
    },
    x: {
      label: "Twitter",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: XIcon,
    },
    whatsapp: {
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      icon: WhatsAppIcon,
    },
    email: {
      label: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%20${encodedUrl}`,
      icon: Mail,
    },
  };
}

type Props = {
  /** Page title used in share text/subject lines. */
  title: string;
  /**
   * Page path to share (e.g. from `pathFor`), resolved against
   * `window.location.origin` on mount. Only used by the `inline` variant —
   * `dropdown` always reads the live `window.location.href` at click time
   * instead (its trigger doubles as the native share-sheet action, so it
   * needs the exact current URL, query string included).
   */
  path?: string;
  className?: string;
  /**
   * `inline` — always-visible row of icon buttons (blog post byline).
   * `dropdown` — a "Share" trigger button that opens a menu, with a native
   * mobile share-sheet fast-path (package detail header).
   */
  variant?: "inline" | "dropdown";
};

/**
 * Social share control. Two presentational modes share the same link-builder,
 * clipboard-copy logic, and brand icon marks so both call sites stay in sync
 * instead of maintaining duplicate share-URL logic.
 */
export function SocialShare({
  title,
  path,
  className,
  variant = "inline",
}: Props) {
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState(() =>
    typeof window === "undefined" || !path ? "" : window.location.origin + path,
  );

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url || window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), variant === "dropdown" ? 1800 : 2000);
    } catch {
      /* ignore — clipboard unavailable */
    }
  }

  if (variant === "dropdown") {
    return (
      <ShareDropdown
        title={title}
        url={url}
        setUrl={setUrl}
        copied={copied}
        copyLink={copyLink}
        className={className}
      />
    );
  }

  const links = buildShareLinks(url, title);
  // Matches the original BlogShare icon order exactly (no Email in this variant).
  const inlineLinks = [links.facebook, links.x, links.whatsapp];

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <span className="text-xs font-mono tracking-wider uppercase text-muted-soft">
        Share
      </span>
      <div className="flex items-center gap-2">
        {inlineLinks.map(({ label, href, icon: Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={`Share on ${label}`}
            className={`flex h-8 w-8 items-center justify-center rounded-full border border-${label.toLowerCase()} text-white transition-colors duration-200 bg-${label.toLowerCase()}`}
          >
            <Icon size={14} className="h-3.5 w-3.5" />
          </a>
        ))}
        <button
          onClick={copyLink}
          title="Copy Link"
          className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-white text-muted hover:border-primary hover:text-primary transition-colors duration-200"
        >
          {copied ? (
            <Check size={14} className="text-success" />
          ) : (
            <Link2 size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

function ShareDropdown({
  title,
  url,
  setUrl,
  copied,
  copyLink,
  className,
}: {
  title: string;
  url: string;
  setUrl: (url: string) => void;
  copied: boolean;
  copyLink: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const links = buildShareLinks(url, title);
  // Matches the original TripActions dropdown order exactly.
  const dropdownLinks = [links.whatsapp, links.facebook, links.x, links.email];

  async function handleShareClick() {
    const href = window.location.href;
    setUrl(href);
    // Prefer the native share sheet on supporting (mostly mobile) browsers.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url: href });
        return;
      } catch {
        // User cancelled or share failed — fall back to the dropdown.
      }
    }
    setOpen((v) => !v);
  }

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={handleShareClick}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full border border-headings/25 bg-white px-4 py-2 text-xs font-bold text-headings transition-colors hover:bg-gray-50"
      >
        <Share2 size={14} className="text-primary" /> Share
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-xl border border-headings/10 bg-white py-1 shadow-lg"
        >
          {dropdownLinks.map(({ label, href, icon: Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-headings transition-colors hover:bg-gray-50"
            >
              <Icon size={16} className="text-primary" />
              {label}
            </a>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={copyLink}
            className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-sm text-headings transition-colors hover:bg-gray-50"
          >
            {copied ? (
              <Check size={16} className="text-primary" />
            ) : (
              <Link2 size={16} className="text-primary" />
            )}
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
